/**
 * Validações alinhadas ao fluxo /cadastrar e ao backend PagBank.
 */

import { onlyDigits, validateCpfDigits } from "./cadastroUtils";
import type { PaymentProfileAddress, PaymentProfileCard } from "../services/pagbank";

export function luhnValid(panDigits: string): boolean {
  const d = onlyDigits(panDigits);
  if (d.length < 13 || d.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = d.length - 1; i >= 0; i--) {
    let n = parseInt(d[i]!, 10);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

export function validatePhoneBrDigits(phone: string): string | null {
  const d = onlyDigits(phone);
  if (d.length < 10 || d.length > 11) {
    return "Informe um celular válido com DDD.";
  }
  return null;
}

export function validateCpfField(cpf: string): string | null {
  if (!validateCpfDigits(cpf)) return "CPF inválido.";
  return null;
}

export function validateAddressComplete(addr: PaymentProfileAddress): string | null {
  const { street, number, district, city, state, postal_code } = addr;
  if (onlyDigits(postal_code).length !== 8) return "CEP inválido.";
  if (!street.trim()) return "Informe a rua.";
  if (!number.trim()) return "Informe o número.";
  if (!district.trim()) return "Informe o bairro.";
  if (!city.trim()) return "Informe a cidade.";
  const st = state.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(st)) return "UF inválida.";
  return null;
}

const HOLDER_NAME_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/;

export function validateHolderName(name: string): string | null {
  const t = name.trim();
  if (t.length < 3) return "Nome do titular inválido.";
  if (!HOLDER_NAME_RE.test(t))
    return "Use apenas letras no nome do titular.";
  return null;
}

export function validateCardPan(numberFormatted: string): string | null {
  const d = onlyDigits(numberFormatted);
  if (d.length < 13 || d.length > 19)
    return "Número do cartão inválido.";
  if (!luhnValid(d)) return "Número do cartão inválido.";
  return null;
}

export function validateExpiryMonth(monthDigits: string): string | null {
  const m = onlyDigits(monthDigits).slice(0, 2);
  if (!m || Number(m) < 1 || Number(m) > 12) return "Mês inválido.";
  return null;
}

/** `yearDigits` = 2 dígitos (AA) como no formulário de pagamento. */
export function validateExpiryYearAa(
  yearAa: string,
  expiryMonthDigits: string,
): string | null {
  const y = onlyDigits(yearAa).slice(0, 2);
  if (y.length !== 2) return "Ano inválido.";
  const month = Number(onlyDigits(expiryMonthDigits).slice(0, 2) || "0");
  if (!month || month < 1 || month > 12) return "Validade inválida.";
  const now = new Date();
  const fullYear = 2000 + Number(y);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (
    fullYear < currentYear ||
    (fullYear === currentYear && month < currentMonth)
  ) {
    return "Cartão vencido. Confira a validade (mês/ano).";
  }
  return null;
}

/** Aceita ano em AA (form) ou YYYY (payload/API). */
export function validateCardNotExpired(
  expiryMonth: string,
  expiryYear: string,
): string | null {
  const em = onlyDigits(expiryMonth).padStart(2, "0").slice(-2);
  const eyRaw = onlyDigits(expiryYear);
  let fullYear: number;
  if (eyRaw.length === 2) {
    fullYear = 2000 + Number(eyRaw);
  } else if (eyRaw.length === 4) {
    fullYear = Number(eyRaw);
  } else {
    return "Validade inválida.";
  }
  const month = Number(em);
  if (!month || month < 1 || month > 12) return "Mês inválido.";
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth() + 1;
  if (fullYear < cy || (fullYear === cy && month < cm)) {
    return "Cartão vencido. Confira a validade (mês/ano).";
  }
  return null;
}

export function validateCvv(cvv: string): string | null {
  const c = onlyDigits(cvv);
  if (c.length < 3 || c.length > 4) return "CVV inválido.";
  return null;
}

export function validateCardStep(card: PaymentProfileCard): string | null {
  const panErr = validateCardPan(card.number);
  if (panErr) return panErr;
  const hn = validateHolderName(card.holder_name);
  if (hn) return hn;
  const mm = validateExpiryMonth(card.expiry_month);
  if (mm) return mm;
  const yy = validateExpiryYearAa(card.expiry_year, card.expiry_month);
  if (yy) return yy;
  const cvv = validateCvv(card.cvv);
  if (cvv) return cvv;
  return null;
}

export type WizardStep = 1 | 2 | 3;

export function validateWizardStep(
  step: WizardStep,
  ctx: {
    cpf: string;
    phone: string;
    addr: PaymentProfileAddress;
    card: PaymentProfileCard;
  },
): string | null {
  if (step === 1) {
    const c = validateCpfField(ctx.cpf);
    if (c) return c;
    return validatePhoneBrDigits(ctx.phone);
  }
  if (step === 2) {
    return validateAddressComplete(ctx.addr);
  }
  return validateCardStep(ctx.card);
}
